/**
 * ÉN KOMPONENT FOR PROFILEN, TRE STEDER DEN BRUKES (QA-runde 12, #7).
 *
 * =================================================================================================
 * ATLES KRAV, ORDRETT: «Personlig profil skal åpne SAMME vindu som kontaktredigeringen for en selv,
 * med samme regler. Én komponent, ikke to skjema.»
 *
 * Derfor er dette ett vindu med to ansikter, ikke to vinduer:
 *
 *   · `mode="onboarding"` — første innlogging. Telefon og språk er PÅKREVD, «Ikke nå» finnes ikke,
 *     og krysset er borte. Serveren svarer `needsOnboarding` og det er den som bestemmer at det
 *     står her; nettleseren regner det ikke ut selv.
 *   · `mode="profile"` — menyen oppe til høyre. Samme felter, samme regler, men man kan lukke.
 *
 * =================================================================================================
 * TRE LAG MED HVER SIN EIER, OG LESEFELT ER IKKE SKJULTE FELT.
 *
 * Tittel, ansvarsområde og «rapporterer til» tilhører HUSET, bak `member_profile:update`. Atle er
 * uttrykkelig på at de likevel skal VISES: «De kan redigeres bare hvis brukeren har rettighet til
 * det i org-en; ellers vises de som lesefelt.» Å skjule dem ville gjort onboarding til et skjema
 * som spør om mindre enn den inviterte allerede er blitt fortalt i invitasjonen.
 *
 * E-posten er alltid låst (#4). Den står ikke engang i `UpdateMyProfileRequest`, så det finnes
 * ingen vei å sende den — låsen her er en forklaring, ikke en sperre.
 */
import type { Locale } from '@tvitvi/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { errorKey } from '@/api/errors';
import { useMe } from '@/api/queries/me';
import { membersQueryOptions } from '@/api/queries/members';
import { myProfileQueryOptions, useUpdateMyProfile } from '@/api/queries/my-profile';
import { allows, useActor } from '@/auth/actor';
import { Callout } from '@/components/ui/Callout';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyValue } from '@/components/ui/EmptyValue';
import { Field } from '@/components/ui/Field';
import { Select } from '@/components/ui/Select';
import { TextField } from '@/components/ui/TextField';
import { useToast } from '@/components/ui/Toast';
import { dynamicKey } from '@/i18n/types';
import { looksLikePhone, splitDisplayName } from '@/lib/split-name';
import { useQuery } from '@tanstack/react-query';

/** Samme sentinel som resten av produktet — se `MemberRoleModal` for hvorfor det ikke er null. */
const REPORTS_TO_NONE = 'none';

export interface MyProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  /** `onboarding` kan ikke lukkes uten å svare. Se filhodet. */
  mode: 'onboarding' | 'profile';
}

/** Lesefeltet husets opplysninger får når kalleren ikke har lov til å skrive dem. */
function ReadOnlyRow({ label, value }: { label: string; value: string | null }): ReactNode {
  return (
    <div className="flex min-h-9 items-center justify-between gap-4 border-b border-subtle last:border-b-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="truncate text-xs text-default">
        {value === null || value === '' ? <EmptyValue /> : value}
      </dd>
    </div>
  );
}

export function MyProfileDialog({
  open,
  onOpenChange,
  organizationId,
  mode,
}: MyProfileDialogProps): ReactNode {
  const { t } = useTranslation();
  const { toast } = useToast();
  const actor = useActor();
  /*
   * EN PERSONLIG KONTO HAR INGEN ROLLE Å VISE (P10, funn B). En ny ekstern uten telefon fikk
   * «Rolle: Superadmin», tittel, ansvarsområde og «Rapporterer til» — det personlige huset, som
   * ingen andre er med i. Rollen og organisasjonens felt står bare i en ekte organisasjon.
   */
  const personal =
    useMe().memberships.find((one) => one.organizationId === organizationId)?.organizationKind ===
    'personal';

  const profile = useQuery({ ...myProfileQueryOptions(), enabled: open });
  /*
   * KOLLEGAENE TRENGS BARE TIL «RAPPORTERER TIL»-VELGEREN, og bare den som får lese dem skal
   * spørre (QA5 A/F M5). Begrenset fikk 403 på `/organization/members` hver gang vinduet åpnet.
   * Lesefeltet bruker `reportsToName` fra profilen og trenger ikke lista.
   */
  const mayListMembers = allows(actor, 'membership:list');
  const members = useQuery({
    ...membersQueryOptions(organizationId, {}),
    enabled: open && !personal && mayListMembers && profile.data?.canEditMembership === true,
  });
  const save = useUpdateMyProfile();

  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [phone, setPhone] = useState('');
  const [locale, setLocale] = useState<Locale | ''>('');
  const [jobTitle, setJobTitle] = useState('');
  const [responsibility, setResponsibility] = useState('');
  const [reportsTo, setReportsTo] = useState(REPORTS_TO_NONE);

  const data = profile.data;

  useEffect(() => {
    if (!open || data === undefined) return;
    /*
     * Kontoer uten todelt navn (alle demobrukerne) fikk to tomme felt og en grå Lagre — man
     * måtte skrive navnet sitt på nytt for å endre telefonen (QA5 A/F L7). Samme reserve som
     * Bli med-siden: del visningsnavnet når de to feltene mangler.
     */
    const split =
      (data.givenName ?? '').trim() === '' && (data.familyName ?? '').trim() === ''
        ? splitDisplayName(data.displayName)
        : { givenName: data.givenName ?? '', familyName: data.familyName ?? '' };
    setGivenName(split.givenName);
    setFamilyName(split.familyName);
    setPhone(data.phone ?? '');
    setLocale(data.preferredLocale ?? '');
    setJobTitle(data.jobTitle ?? '');
    setResponsibility(data.responsibility ?? '');
    setReportsTo(data.reportsToMembershipId ?? REPORTS_TO_NONE);
    // Å åpne vinduet er det som fyller det. Svaret er derivert og ville ellers løpt rundt.
  }, [open, data]);

  const mayEditHouse = !personal && data?.canEditMembership === true;

  /*
   * PÅKREVD BETYR PÅKREVD, OG DET ER SERVERENS DEFINISJON. `needsOnboarding` er telefon ELLER
   * språk som mangler; knappen her sperrer på nøyaktig det samme, så man aldri får en 400 for noe
   * skjemaet kunne sagt fra om selv.
   */
  /*
   * OG DEN SIER HVA SOM MANGLER (J4, QA14 runde 2 Q1).
   *
   * «Lagre» sto grå uten et ord om hvorfor. Det er den samme feilen som på invitasjonssida, og den
   * samme rettingen: grunnen skal stå som TEKST i footeren, ikke i en `title` ingen telefon kan
   * hente ut. Rekkefølgen er den man fyller ut i, så den peker på det første som mangler.
   */
  const phoneInvalid = phone.trim() !== '' && !looksLikePhone(phone);
  const incomplete =
    givenName.trim() === '' || phone.trim() === '' || phoneInvalid || locale === '';
  const missing =
    givenName.trim() === ''
      ? t('profile.cannot.givenName')
      : phone.trim() === ''
        ? t('profile.cannot.phone')
        : phoneInvalid
          ? t('profile.cannot.phoneInvalid')
          : locale === ''
            ? t('profile.cannot.locale')
            : null;
  const colleagues = (members.data?.items ?? []).filter((one) => one.id !== data?.membershipId);

  async function submit(): Promise<void> {
    if (incomplete) return;
    try {
      await save.mutateAsync({
        givenName: givenName.trim(),
        familyName: familyName.trim(),
        phone: phone.trim() === '' ? null : phone.trim(),
        // `incomplete` har alt utelukket den tomme her, og TypeScript vet det.
        preferredLocale: locale,
        ...(mayEditHouse
          ? {
              jobTitle: jobTitle.trim() === '' ? null : jobTitle.trim(),
              responsibility: responsibility.trim() === '' ? null : responsibility.trim(),
              reportsToMembershipId: reportsTo === REPORTS_TO_NONE ? null : reportsTo,
            }
          : {}),
      });
      toast({ title: t('profile.saved'), tone: 'success' });
      onOpenChange(false);
    } catch {
      // Vises i vinduet, ved siden av feltene som forårsaket den.
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={mode === 'onboarding' ? () => undefined : onOpenChange}
      title={t(mode === 'onboarding' ? 'profile.onboarding.title' : 'profile.edit.title')}
      description={t(
        mode === 'onboarding' ? 'profile.onboarding.description' : 'profile.edit.description',
      )}
      /* Onboarding har ingen vei ut uten å svare: telefon og språk er det produktet trenger. */
      cancelLabel={mode === 'onboarding' ? null : undefined}
      /*
       * OG DA SKAL DET IKKE STÅ ET KRYSS DER HELLER (Q1).
       * `onOpenChange` er en tom funksjon i denne modusen, så krysset gjorde ingenting. Atle
       * meldte det som «Lukk lukker ikke» — og en knapp som ikke virker er verre enn ingen knapp.
       */
      dismissible={mode !== 'onboarding'}
      {...(missing === null
        ? {}
        : {
            footerStart: (
              <span className="text-xs text-muted" data-testid="profile-missing">
                {missing}
              </span>
            ),
          })}
      primary={{
        label: t('common.actions.save'),
        onAction: () => void submit(),
        loading: save.isPending,
        disabled: incomplete,
        'data-testid': 'profile-save',
      }}
      data-testid="my-profile-dialog"
    >
      <div className="flex flex-col gap-4">
        {save.error != null ? <Callout tone="danger">{t(errorKey(save.error))}</Callout> : null}

        {/*
          TO FELT, FORDI NAVNET ER TO TING (QA15 2b).

          Ett felt tvang serveren til å gjette hvor fornavnet slutter, og gjetningen var en deling
          ved siste mellomrom: «Anne Kari Dahl Hansen» ble fornavn «Anne Kari Dahl». Avsenderen vet
          svaret; da er det billigere å spørre enn å regne.

          Etternavn er valgfritt. Ikke alle har ett, og et påkrevd felt er en påstand om verden.
        */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('profile.field.givenName')}>
            {(control) => (
              <TextField
                {...control}
                value={givenName}
                onChange={(event) => setGivenName(event.target.value)}
                data-testid="profile-given-name"
              />
            )}
          </Field>

          <Field label={t('profile.field.familyName')}>
            {(control) => (
              <TextField
                {...control}
                value={familyName}
                onChange={(event) => setFamilyName(event.target.value)}
                data-testid="profile-family-name"
              />
            )}
          </Field>
        </div>

        <Field
          label={t('profile.field.phone')}
          description={t('profile.field.phoneHelp')}
          {...(phoneInvalid ? { error: t('profile.cannot.phoneInvalid') } : {})}
        >
          {(control) => (
            <TextField
              {...control}
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              data-testid="profile-phone"
            />
          )}
        </Field>

        <Field label={t('profile.field.locale')}>
          {(control) => (
            <Select
              {...control}
              value={locale}
              onValueChange={(value) => setLocale(value as Locale)}
              placeholder={t('profile.field.localePlaceholder')}
              data-testid="profile-locale"
              options={[
                { value: 'nb', label: t(dynamicKey('enums.locale.nb')) },
                { value: 'en', label: t(dynamicKey('enums.locale.en')) },
              ]}
            />
          )}
        </Field>

        {/* Låst, og den sier hvorfor framfor bare å være grå (#4). */}
        <dl className="flex flex-col">
          <ReadOnlyRow label={t('profile.field.email')} value={data?.email ?? null} />
          {personal ? null : (
            <ReadOnlyRow
              label={t('profile.field.role')}
              value={data?.role == null ? null : t(dynamicKey(`enums.org_role.${data.role}`))}
            />
          )}
        </dl>
        <p className="text-2xs text-subtle" data-testid="profile-email-locked">
          {t('profile.field.emailLocked')}
        </p>

        {personal ? null : mayEditHouse ? (
          <>
            <Field label={t('contacts.invite.jobTitle')} optionalLabel={t('common.optional')}>
              {(control) => (
                <TextField
                  {...control}
                  value={jobTitle}
                  onChange={(event) => setJobTitle(event.target.value)}
                  data-testid="profile-job-title"
                />
              )}
            </Field>

            <Field label={t('contacts.invite.responsibility')} optionalLabel={t('common.optional')}>
              {(control) => (
                <TextField
                  {...control}
                  value={responsibility}
                  onChange={(event) => setResponsibility(event.target.value)}
                  data-testid="profile-responsibility"
                />
              )}
            </Field>

            <Field
              label={t('contacts.organization.reportsTo')}
              optionalLabel={t('common.optional')}
            >
              {(control) => (
                <Select
                  {...control}
                  value={reportsTo}
                  onValueChange={setReportsTo}
                  data-testid="profile-reports-to"
                  options={[
                    { value: REPORTS_TO_NONE, label: t('contacts.organization.reportsToNobody') },
                    ...colleagues.map((one) => ({ value: one.id, label: one.displayName })),
                  ]}
                />
              )}
            </Field>
          </>
        ) : (
          /* Vist, ikke skjult — se filhodet. Huset har satt dem, og du skal få vite hva. */
          <dl className="flex flex-col" data-testid="profile-house-readonly">
            <ReadOnlyRow label={t('contacts.invite.jobTitle')} value={data?.jobTitle ?? null} />
            <ReadOnlyRow
              label={t('contacts.invite.responsibility')}
              value={data?.responsibility ?? null}
            />
            <ReadOnlyRow
              label={t('contacts.organization.reportsTo')}
              value={data?.reportsToName ?? null}
            />
          </dl>
        )}
      </div>
    </Dialog>
  );
}
